import BaseApiService from './BaseApiService';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { apiBaseUrl, protectedApiPath } from '../config/ServiceConfig';

/**
 * API service for protected endpoints that require authentication
 * All requests go to /protected/ endpoints and always include auth headers
 */
class ProtectedApiService extends BaseApiService {
    constructor() {
        super(`${apiBaseUrl}${protectedApiPath}`);
    }

    /**
     * Get authentication headers using Amplify v6 APIs
     */
    private async getAuthHeaders(): Promise<{ headers: { Authorization?: string } }> {
        console.log('🔍 [DEBUG] ProtectedApiService.getAuthHeaders - Starting auth header retrieval');
        
        try {
            // Method 1: Try to get current session using v6 API
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();
            
            if (idToken) {
                console.log('🔍 [DEBUG] Using current session token from v6 API');
                return { headers: { 'Authorization': idToken } };
            }
        } catch (err: any) {
            console.log('🔍 [DEBUG] Current session failed:', err.message);
        }

        // Method 2: Try to get current user and then session
        try {
            const currentUser = await getCurrentUser();
            if (currentUser) {
                console.log('🔍 [DEBUG] Found authenticated user, attempting to get session');
                const session = await fetchAuthSession();
                const idToken = session.tokens?.idToken?.toString();
                
                if (idToken) {
                    console.log('🔍 [DEBUG] Using authenticated user session token');
                    return { headers: { 'Authorization': idToken } };
                }
            }
        } catch (err: any) {
            console.log('🔍 [DEBUG] Authenticated user method failed:', err.message);
        }

        // Method 3: Try stored session fallback (for backward compatibility)
        try {
            const storedSession = localStorage.getItem('amplify-auth-session');
            if (storedSession) {
                const authData = JSON.parse(storedSession);
                
                // Verify token is still valid
                const tokenPayload = JSON.parse(atob(authData.idToken.split('.')[1]));
                const now = Math.floor(Date.now() / 1000);
                
                if (tokenPayload.exp && tokenPayload.exp > now) {
                    console.log('🔍 [DEBUG] Using stored auth session for API request');
                    return { headers: { 'Authorization': authData.idToken } };
                } else {
                    console.log('🔍 [DEBUG] Stored auth session expired, clearing it');
                    localStorage.removeItem('amplify-auth-session');
                }
            }
        } catch (error) {
            console.error('❌ [DEBUG] Error reading stored auth session:', error);
        }
        
        console.error('❌ [DEBUG] All authentication methods failed');
        return { headers: {} };
    }

    async getRequest(endpoint: string, params?: any): Promise<any> {
        this.logRequest('GET', endpoint);
        try {
            const authHeaders = await this.getAuthHeaders();
            console.log('🔍 [DEBUG] Auth headers:', authHeaders.headers.Authorization ? 'Present' : 'Missing');
            
            const response = await this.httpClient.get(`${this.baseUrl}${endpoint}`, { 
                ...authHeaders, 
                params 
            });
            this.logResponse('GET', response.status, response.data);
            return response.data;
        } catch (error) {
            this.handleError(error, 'ProtectedApiService.GET');
        }
    }

    async postRequest(endpoint: string, data?: any): Promise<any> {
        this.logRequest('POST', endpoint, data);
        try {
            const authHeaders = await this.getAuthHeaders();
            console.log('🔍 [DEBUG] Auth headers:', authHeaders.headers.Authorization ? 'Present' : 'Missing');
            
            const response = await this.httpClient.post(`${this.baseUrl}${endpoint}`, data, authHeaders);
            this.logResponse('POST', response.status, response.data);
            return response.data;
        } catch (error) {
            this.handleError(error, 'ProtectedApiService.POST');
        }
    }

    async putRequest(endpoint: string, data?: any): Promise<any> {
        this.logRequest('PUT', endpoint, data);
        try {
            const authHeaders = await this.getAuthHeaders();
            console.log('🔍 [DEBUG] PUT Auth headers:', authHeaders.headers.Authorization ? 'Present' : 'Missing');
            
            const response = await this.httpClient.put(`${this.baseUrl}${endpoint}`, data, authHeaders);
            this.logResponse('PUT', response.status, response.data);
            return response.data;
        } catch (error: any) {
            console.error('❌ [DEBUG] PUT request failed:', {
                url: `${this.baseUrl}${endpoint}`,
                requestData: data,
                error: error.message,
                status: error.response?.status,
                responseData: error.response?.data
            });
            
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            
            throw error;
        }
    }
}

export default ProtectedApiService;
